/**
 * ProjectSectionDocs - inline uploader + document list for a project section.
 * Lets a user drop MahaRERA / NCLT / legal / technical PDFs directly on the
 * section they're reading, saves to the Document Vault pre-tagged with the
 * right category + project_id.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { FilePlus, FileText, Download, Loader2, Upload, Tag, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { uploadToVault, apiErrorText, openVaultFile } from '@/lib/vaultUpload';

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Props:
 *   projectId        — required
 *   defaultCategory  — vault category id (rera / nclt / corpus / legal_notices / society / project_updates / contracts / approvals / public_records / general)
 *   allowedCategories — optional array of category ids to filter the listing
 *   title            — short friendly heading, e.g., "Attach MahaRERA records"
 *   subtitle         — short hint, e.g., "Upload QPR PDFs, order copies, approvals"
 */
export default function ProjectSectionDocs({
  projectId,
  defaultCategory = 'general',
  allowedCategories,
  title = 'Attach documents',
  subtitle = 'Upload PDFs, scans or images. They land in the project Document Vault.',
  emptyText,
}) {
  const { user, token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    category: defaultCategory,
    title: '',
    description: '',
    tags: '',
    source_url: '',
    visibility: 'project_members',
    file: null,
  });

  const effectiveCats = allowedCategories && allowedCategories.length
    ? allowedCategories
    : [defaultCategory];
  const catsKey = effectiveCats.join(',');

  const loadAll = async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [catsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/document-vault/categories`),
        axios.get(`${API}/api/document-vault`, { params: { project_id: projectId, limit: 50 } }),
      ]);
      setCategories(catsRes.data.categories || []);
      const filtered = (docsRes.data.documents || []).filter((d) => effectiveCats.includes(d.category));
      setDocs(filtered);
    } catch (e) {
      console.error('Section docs load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, catsKey]);

  const handleUpload = async () => {
    if (!token) {
      toast.error('Please sign in to upload');
      return;
    }
    if (!form.file) {
      toast.error('Choose a file to upload');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('project_id', projectId);
      fd.append('category', form.category);
      if (form.title.trim()) fd.append('title', form.title.trim());
      fd.append('tags', form.tags || '');
      fd.append('source_url', form.source_url || '');
      fd.append('visibility', form.visibility);
      const res = await uploadToVault({ formData: fd, headers: { Authorization: `Bearer ${token}` } });
      if (!res) return;
      toast.success('Added to project vault. AI is naming and summarising it now.');
      setOpen(false);
      setForm({ ...form, title: '', description: '', tags: '', source_url: '', file: null });
      loadAll();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setUploading(false);
    }
  };

  const canUpload = !!user && !!projectId;

  return (
    <Card data-testid="project-section-docs" className="border-dashed">
      <CardContent className="py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">{title}</h3>
              <Badge variant="secondary" className="text-[10px]">{docs.length} on file</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={!canUpload} data-testid="section-docs-upload-btn">
                <FilePlus className="h-4 w-4 mr-1" /> Add document
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{subtitle}</DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <div>
                  <Label>File *</Label>
                  <Input
                    data-testid="section-docs-file"
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,.txt,.csv"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF, Word, Excel, images — up to 25 MB. AI names and summarises it for you.
                  </p>
                </div>

                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="section-docs-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories
                        .filter((c) => !allowedCategories || allowedCategories.includes(c.id))
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Who can see it</Label>
                  <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                    <SelectTrigger data-testid="section-docs-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_members">Project members only</SelectItem>
                      <SelectItem value="public">Public — anyone on the internet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <details className="rounded-md border px-3 py-2" data-testid="section-docs-more">
                  <summary className="text-xs font-medium cursor-pointer select-none">More details (optional)</summary>
                  <div className="space-y-3 pt-3">
                    <div>
                      <Label>Title</Label>
                      <Input
                        data-testid="section-docs-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Leave blank to let AI name it"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tags <span className="text-xs text-muted-foreground">(comma)</span></Label>
                        <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="qpr, 2025" />
                      </div>
                      <div>
                        <Label>Source link</Label>
                        <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="Portal link" />
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading} data-testid="section-docs-submit">
                  {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</>) : 'Upload'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {!canUpload && (
          <p className="text-xs text-muted-foreground">Sign in as a verified project member to add documents.</p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : docs.length === 0 ? (
          <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
            {emptyText || 'Nothing uploaded here yet. Start by dropping your MahaRERA / NCLT / approval PDFs.'}
          </div>
        ) : (
          <div className="space-y-2">
            {docs.slice(0, 6).map((d) => (
              <div key={d.id} className="flex items-start gap-2 p-2 rounded-md border bg-background">
                <FileText className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.title}</p>
                  {d.ai_gist_status === 'pending' && <p className="text-[11px] text-muted-foreground italic">AI is reading this document…</p>}
                  {d.ai_gist && (
                    <details className="mt-1" data-testid={`section-docs-gist-${d.id}`}>
                      <summary className="text-[11px] text-primary cursor-pointer select-none">Plain-language summary</summary>
                      <p className="text-xs text-muted-foreground whitespace-pre-line mt-1">{d.ai_gist.replace(/\*\*/g, '')}</p>
                    </details>
                  )}
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                    <span>{d.uploaded_by_name || 'Member'}</span>
                    <span>·</span>
                    <span>{new Date(d.document_date || d.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    {d.size ? <><span>·</span><span>{formatBytes(d.size)}</span></> : null}
                    {d.tags?.slice(0, 3).map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px] py-0 h-4">
                        <Tag className="h-2.5 w-2.5 mr-0.5" />{t}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => openVaultFile(d.storage_path).catch(() => toast.error('Could not open this document'))} data-testid={`section-docs-open-${d.id}`}>
                  <Download className="h-4 w-4" />
                </Button>
                {d.source_url && (
                  <Button size="icon" variant="ghost" asChild>
                    <a href={d.source_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
              </div>
            ))}
            {docs.length > 6 && (
              <p className="text-xs text-muted-foreground text-center">+ {docs.length - 6} more — see Document Vault</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
